import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, SpinnerGap, XCircle } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    async function pollStatus() {
      if (attempts >= maxAttempts) {
        setStatus("timeout");
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentData(res.data);

        if (res.data.payment_status === "paid") {
          setStatus("success");
          return;
        } else if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }

        attempts++;
        setTimeout(pollStatus, pollInterval);
      } catch (err) {
        console.error("Error checking payment status:", err);
        attempts++;
        setTimeout(pollStatus, pollInterval);
      }
    }

    pollStatus();
  }, [sessionId]);

  return (
    <section className="min-h-screen flex items-center justify-center px-6 pt-24" style={{ backgroundColor: '#0C0C0C' }} data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full text-center"
      >
        {status === "checking" && (
          <div className="space-y-6" data-testid="payment-checking">
            <SpinnerGap size={48} weight="regular" className="animate-spin mx-auto" style={{ color: '#FF5A36' }} />
            <h2 className="font-heading text-3xl font-light" style={{ color: '#F0EDE5' }}>
              Checking Payment...
            </h2>
            <p className="font-body text-sm" style={{ color: '#9A9A9A' }}>
              Please wait while we verify your payment.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6" data-testid="payment-success">
            <CheckCircle size={64} weight="regular" className="mx-auto" style={{ color: '#22c55e' }} />
            <h2 className="font-heading text-3xl sm:text-4xl font-light" style={{ color: '#F0EDE5' }}>
              Order Confirmed!
            </h2>
            <p className="font-body text-base leading-relaxed" style={{ color: '#9A9A9A' }}>
              Thank you for your purchase. Your DermaSand bars are on their way.
              You'll receive a confirmation email shortly.
            </p>
            {paymentData && (
              <p className="font-accent text-sm" style={{ color: '#9A9A9A' }}>
                Amount paid: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
              </p>
            )}
            <a
              href="/"
              className="inline-flex items-center gap-2 px-10 py-4 mt-4 text-sm font-accent font-semibold tracking-wider uppercase text-white transition-all duration-300 hover:brightness-110"
              style={{ backgroundColor: '#FF5A36' }}
              data-testid="back-to-shop-btn"
            >
              Back to Shop
            </a>
          </div>
        )}

        {(status === "error" || status === "expired" || status === "timeout") && (
          <div className="space-y-6" data-testid="payment-error">
            <XCircle size={64} weight="regular" className="mx-auto" style={{ color: '#ef4444' }} />
            <h2 className="font-heading text-3xl font-light" style={{ color: '#F0EDE5' }}>
              {status === "timeout" ? "Payment Pending" : "Something Went Wrong"}
            </h2>
            <p className="font-body text-base leading-relaxed" style={{ color: '#9A9A9A' }}>
              {status === "timeout"
                ? "We couldn't confirm your payment yet. Please check your email for a confirmation."
                : "There was an issue processing your payment. Please try again."}
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-10 py-4 mt-4 text-sm font-accent font-semibold tracking-wider uppercase text-white transition-all duration-300 hover:brightness-110"
              style={{ backgroundColor: '#FF5A36' }}
              data-testid="try-again-btn"
            >
              Back to Shop
            </a>
          </div>
        )}
      </motion.div>
    </section>
  );
}
